import React, { useState, useCallback } from 'react';
import { FiUpload, FiFile, FiAlertCircle } from 'react-icons/fi';
import Papa from 'papaparse';

interface CsvRow {
  [key: string]: string;
}

interface UploadStepProps {
  onComplete: (rows: CsvRow[], mapping: Record<string, string>) => void;
  onClose: () => void;
}

export const UploadStep: React.FC<UploadStepProps> = ({
  onComplete,
  onClose
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processFile = useCallback(async (file: File) => {
    console.log('=== PROCESS FILE DEBUG ===');
    console.log('File name:', file.name);
    console.log('File size:', file.size, 'bytes');
    console.log('File type:', file.type);
    console.log('File lastModified:', new Date(file.lastModified));
    
    setIsProcessing(true);
    setError(null);

    try {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        console.log('❌ File rejected: Size exceeds 5MB limit');
        throw new Error('File size exceeds 5MB limit');
      }
      console.log('✅ File size check passed');

      // Check file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        console.log('❌ File rejected: Not a CSV file');
        throw new Error('Please select a CSV file');
      }
      console.log('✅ File type check passed');

      console.log('📖 Reading file text...');
      const text = await file.text();
      console.log('📄 File text length:', text.length);
      console.log('📄 First 500 chars:', text.substring(0, 500));
      
      return new Promise<{ rows: CsvRow[], mapping: Record<string, string> }>((resolve, reject) => {
        console.log('🔄 Starting Papa.parse...');
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results: Papa.ParseResult<CsvRow>) => {
            console.log('📊 Papa.parse complete');
            console.log('📊 Results meta:', results.meta);
            console.log('📊 Number of rows:', results.data.length);
            console.log('📊 First row:', results.data[0]);
            console.log('📊 Errors:', results.errors);
            
            if (results.errors.length > 0) {
              console.log('❌ Papa.parse errors found:', results.errors);
              // Build a concise, user-visible error message including first few row numbers and messages
              const details = results.errors.slice(0, 3).map((e) => {
                const rowIsNumber = typeof (e as any).row === 'number';
                const rowNum = rowIsNumber ? ((e as any).row as number) + 1 : null;
                const prefix = rowNum ? `Row ${rowNum}: ` : '';
                const message = (e as any).message || (e as any).code || 'CSV parse error';
                return `${prefix}${message}`;
              }).join('; ');
              const err = new Error(details || 'Failed to parse CSV file');
              reject(err);
              return;
            }

            const rows = results.data.slice(0, 25000); // 25k row limit
            console.log('📊 Rows after slice:', rows.length);
            
            const mapping = generateColumnMapping(rows[0] || {});
            console.log('🗺️ Generated mapping:', mapping);

            console.log('✅ File processing successful');
            resolve({ rows, mapping });
          },
          error: (error: any) => {
            console.log('❌ Papa.parse error:', error);
            reject(new Error('Failed to parse CSV file'));
          }
        });
      });

    } catch (err) {
      console.log('❌ Process file error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to process file';
      console.log('❌ Setting error message:', errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      console.log('🏁 Process file finally block');
      setIsProcessing(false);
    }
  }, []);

  const generateColumnMapping = (firstRow: CsvRow): Record<string, string> => {
    console.log('🗺️ generateColumnMapping called with firstRow:', firstRow);
    const columnMap: Record<string, string> = {};
    const expectedFields = [
      // Required fields
      'display_name', 'billing_addr_line1', 'billing_addr_city', 'billing_addr_state', 'billing_addr_zip',
      // Personal information
      'first_name', 'last_name', 'title', 'suffix', 'company_name', 'website', 'notes',
      // Contact information
      'email', 'phone', 'mobile', 'fax', 'cc_email', 'bcc_email', 'name_on_checks',
      // Billing address
      'billing_addr_line2', 'billing_addr_country',
      // Shipping address
      'shipping_addr_line1', 'shipping_addr_line2', 'shipping_addr_city', 'shipping_addr_state', 
      'shipping_addr_zip', 'shipping_addr_country', 'shipping_same_as_billing',
      // Tax information
      'tax_exempt', 'tax_exempt_doc_url'
    ];

    // Synonym mappings
    const synonyms: Record<string, string> = {
      'given_name': 'first_name',
      'surname': 'last_name', 
      'work phone': 'phone',
      'cell': 'mobile',
      'cell phone': 'mobile',
      'address': 'billing_addr_line1',
      'street': 'billing_addr_line1',
      'city': 'billing_addr_city',
      'state': 'billing_addr_state',
      'zip': 'billing_addr_zip',
      'zipcode': 'billing_addr_zip',
      'postal_code': 'billing_addr_zip',
      'country': 'billing_addr_country',
      'shipping_address': 'shipping_addr_line1',
      'shipping_street': 'shipping_addr_line1',
      'shipping_city': 'shipping_addr_city',
      'shipping_state': 'shipping_addr_state',
      'shipping_zip': 'shipping_addr_zip',
      'shipping_country': 'shipping_addr_country',
      'tax_exemption': 'tax_exempt',
      'tax_exempt_document': 'tax_exempt_doc_url',
      'cc_email_address': 'cc_email',
      'bcc_email_address': 'bcc_email',
      'check_name': 'name_on_checks',
      'company': 'company_name',
      'business_name': 'company_name',
      'web_site': 'website',
      'web': 'website',
      'url': 'website'
    };

    Object.keys(firstRow).forEach(csvHeader => {
      console.log('🔍 Processing CSV header:', csvHeader);
      
      // Case-insensitive, space/underscore-agnostic matching
      const normalizedHeader = csvHeader.toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      
      console.log('🔍 Normalized header:', normalizedHeader);
      
      // Try exact match first
      if (expectedFields.includes(normalizedHeader)) {
        console.log('✅ Exact match found for:', normalizedHeader);
        columnMap[normalizedHeader] = csvHeader;
        return;
      }

      // Try synonym matching
      if (synonyms[normalizedHeader]) {
        console.log('🔄 Synonym match found:', normalizedHeader, '->', synonyms[normalizedHeader]);
        columnMap[synonyms[normalizedHeader]] = csvHeader;
        return;
      }

      // Disable fuzzy matching to prevent incorrect matches
      // Only use exact matches and synonyms
      console.log('❌ No exact match or synonym found for:', normalizedHeader);
    });

    console.log('🗺️ All CSV headers:', Object.keys(firstRow));
    console.log('🗺️ Expected fields:', expectedFields);
    console.log('🗺️ Final column mapping:', columnMap);
    
    // Force correct mapping for known CSV headers
    const csvHeaders = Object.keys(firstRow);
    if (csvHeaders.includes('email')) {
      columnMap.email = 'email';
    }
    if (csvHeaders.includes('phone')) {
      columnMap.phone = 'phone';
    }
    if (csvHeaders.includes('mobile')) {
      columnMap.mobile = 'mobile';
    }
    
    console.log('🗺️ Final column mapping after override:', columnMap);
    return columnMap;
  };

  const handleFileSelect = useCallback(async (file: File) => {
    console.log('🎯 handleFileSelect called with file:', file.name);
    try {
      const { rows, mapping } = await processFile(file);
      console.log('✅ handleFileSelect success, calling onComplete');
      onComplete(rows, mapping);
    } catch (err) {
      console.log('❌ handleFileSelect error:', err);
      // Error already set in processFile
    }
  }, [processFile, onComplete]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  return (
    <div className="p-6 pb-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
            Upload Customer CSV File
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Drag and drop your CSV file here, or click to browse
          </p>
        </div>

        {/* Upload Area */}
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragOver 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
              : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500'
            }
            ${isProcessing ? 'pointer-events-none opacity-50' : ''}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".csv"
            onChange={handleFileInput}
            className="hidden"
          />
          
          {isProcessing ? (
            <div className="space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-neutral-600 dark:text-neutral-400">
                Processing CSV file...
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <FiUpload className="mx-auto h-12 w-12 text-neutral-400" />
              <div>
                <p className="text-neutral-900 dark:text-neutral-50 font-medium">
                  Drop your CSV file here
                </p>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm mt-1">
                  or click to browse
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex items-center">
              <FiAlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Requirements */}
        <div className="mt-8 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-md">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-neutral-900 dark:text-neutral-50">
              File Requirements
            </h4>
            <a
              href="/sample-customers.csv"
              download="sample-customers.csv"
              className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <FiFile className="mr-1 h-4 w-4" />
              Download Sample CSV
            </a>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-medium text-neutral-900 dark:text-neutral-50 mb-2 text-sm">
                File Limits
              </h5>
              <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                <li>• Maximum file size: 5MB</li>
                <li>• Maximum rows: 25,000</li>
                <li>• Supported formats: CSV only</li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-medium text-neutral-900 dark:text-neutral-50 mb-2 text-sm">
                Required Fields <span className="text-red-500">*</span>
              </h5>
              <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                <li>• <span className="font-medium">Display Name</span> - Customer's display name</li>
                <li>• <span className="font-medium">Billing Address</span> - Street, City, State, ZIP</li>
                <li>• <span className="font-medium">Contact</span> - Email OR Phone OR Mobile required</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <h5 className="font-medium text-neutral-900 dark:text-neutral-50 mb-2 text-sm">
              Optional Fields (30 total fields supported)
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs text-neutral-600 dark:text-neutral-400">
              <div>
                <span className="font-medium">Personal Info:</span>
                <ul className="mt-1 space-y-0.5">
                  <li>• First Name, Last Name</li>
                  <li>• Title, Suffix</li>
                  <li>• Company Name</li>
                  <li>• Website, Notes</li>
                </ul>
              </div>
              <div>
                <span className="font-medium">Contact:</span>
                <ul className="mt-1 space-y-0.5">
                  <li>• Mobile, Fax</li>
                  <li>• CC Email, BCC Email</li>
                  <li>• Name on Checks</li>
                </ul>
              </div>
              <div>
                <span className="font-medium">Address:</span>
                <ul className="mt-1 space-y-0.5">
                  <li>• Billing Address Line 2</li>
                  <li>• Shipping Address (all fields)</li>
                  <li>• Same as Billing flag</li>
                </ul>
              </div>
              <div>
                <span className="font-medium">Tax & Business:</span>
                <ul className="mt-1 space-y-0.5">
                  <li>• Tax Exempt Status</li>
                  <li>• Tax Exempt Doc URL</li>
                  <li>• Country (billing/shipping)</li>
                  <li>• Starting Balance (positive shows as overdue; negative is credit)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}; 